import React, { useState, useEffect, useRef, useMemo } from 'react';
import ConfirmDialog from './ConfirmDialog';
import BaseDialog from './BaseDialog';
import Button from './Button';
import MultiSelect from './MultiSelect';
import templateService, { getTemplatesForType } from '../../services/templateService';
import config from '../../config/environment';
import { obfuscateEmail } from '../../utils/emailObfuscation';
import FilePreviewDialog from './FilePreviewDialog';

// Helper function to construct full file URL from relative path
const getFileUrl = (filePath) => {
  if (!filePath) return '';

  // If already a full URL, return as is
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath;
  }

  // Get base URL without /api suffix
  const baseURL = config.api.baseURL.replace('/api', '');

  // Construct full URL - Laravel typically serves files from /storage/
  // Remove leading slash if present to avoid double slashes
  const cleanPath = filePath.startsWith('/') ? filePath.substring(1) : filePath;

  // Construct URL: baseURL/storage/filePath
  return `${baseURL}/storage/${cleanPath}`;
};

/**
 * AuthorityEditComplaintDialog Component
 * 
 * A dedicated dialog for Authority users to edit complaints
 * Shows CVO-assigned fields as read-only, allows type selection, template selection, and submission
 * 
 * @param {boolean} isOpen - Controls dialog visibility
 * @param {object} complaint - Complaint data object
 * @param {array} complaintStatuses - List of available statuses
 * @param {array} complaintTypes - List of available complaint types
 * @param {array} departments - List of available departments
 * @param {array} officers - List of officers to assign
 * @param {array} priorities - List of priority options
 * @param {array} complaintLogs - Complaint history logs
 * @param {boolean} logsLoading - Loading state for logs
 * @param {string} newNote - Current note input value
 * @param {function} onNoteChange - Callback when note changes
 * @param {function} onAddNote - Callback to add a note
 * @param {function} onSave - Callback when user saves changes
 * @param {function} onClose - Callback when dialog closes
 * @param {boolean} showHistory - Whether to show full history section (default: true)
 * @param {boolean} loading - Loading state for async operations
 */
const AuthorityEditComplaintDialog = ({
  isOpen,
  complaint,
  complaintStatuses = [],
  complaintTypes = [],
  departments = [],
  officers = [],
  priorities = [],
  complaintLogs = [],
  logsLoading = false,
  newNote = '',
  onNoteChange,
  onAddNote,
  onSave,
  onClose,
  showHistory = true,
  loading = false,
}) => {
  const [selectedType, setSelectedType] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [showSubmitConfirmDialog, setShowSubmitConfirmDialog] = useState(false);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [templatePreviewContent, setTemplatePreviewContent] = useState('');
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState([]);

  const [noteAttachments, setNoteAttachments] = useState([]);

  const [previewDialog, setPreviewDialog] = useState({
    isOpen: false,
    fileUrl: '',
    fileName: '',
    fileType: ''
  });

  const handlePreviewFile = (fileOrUrl, name) => {
    if (fileOrUrl instanceof File) {
      const url = URL.createObjectURL(fileOrUrl);
      setPreviewDialog({
        isOpen: true,
        fileUrl: url,
        fileName: fileOrUrl.name,
        fileType: fileOrUrl.type
      });
    } else if (typeof fileOrUrl === 'string') {
      const fullUrl = getFileUrl(fileOrUrl);
      setPreviewDialog({
        isOpen: true,
        fileUrl: fullUrl,
        fileName: name || fileOrUrl.split('/').pop() || 'Document',
        fileType: '' // Type unknown from URL
      });
    }
  };

  const closePreviewDialog = () => {
    if (previewDialog.fileUrl && previewDialog.fileUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewDialog.fileUrl);
    }
    setPreviewDialog({ ...previewDialog, isOpen: false });
  };

  // Helper function to normalize strings for comparison (case-insensitive)
  const normalizeString = (str) => {
    if (!str) return '';
    return String(str).trim().toLowerCase();
  };

  // Helper function to get option value
  const getOptionValue = (item) => {
    return typeof item === 'object' ? item.name : item;
  };

  // Helper function to check if option matches current value
  const isSelected = (optionValue, currentValue) => {
    return normalizeString(optionValue) === normalizeString(currentValue);
  };

  // Initialize type and template when complaint changes
  useEffect(() => {
    if (isOpen && complaint) {
      // Prioritize assign_complain_type as that's the key from backend
      const complaintType = (complaint?.assign_complaint_type?.name || '').trim();
      // Set selectedType (can be empty string if no type)
      setSelectedType(complaintType);
      if (complaintType) {
        const templates = getTemplatesForType(complaintType);
        setTemplateOptions(templates);
        setSelectedTemplate(complaint?.selected_template || '');
      }
    } else if (!isOpen) {
      // Reset when dialog closes
      setSelectedType('');
      setTemplateOptions([]);
      setSelectedTemplate('');
    }
  }, [isOpen, complaint]);

  // Check if complaint already has a type assigned (from database)
  // Prioritize assign_complain_type as that's the key from backend
  // Only consider it existing if it's a non-empty string
  // If it does, form is in view-only mode (disabled)
  const existingType = (complaint?.assign_complain_type || complaint?.type || '').trim();
  const hasExistingType = existingType !== '';
  const isFormDisabled = hasExistingType;

  // Check if user has selected a type in the current session
  const hasSelectedType = !!selectedType && selectedType.trim() !== '';

  // Update template options when type changes
  useEffect(() => {
    if (selectedType) {
      const templates = getTemplatesForType(selectedType);
      setTemplateOptions(templates);
      // Reset template selection when type changes
      if (templates.length > 0 && templates[0].value !== 'no-template') {
        setSelectedTemplate(templates[0].value);
      } else {
        setSelectedTemplate('');
      }
    }
  }, [selectedType]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setShowSubmitConfirmDialog(false);
      setIsHistoryDialogOpen(false);
    }
  }, [isOpen]);

  // Handle submit button click
  const handleSubmitClick = () => {
    setShowSubmitConfirmDialog(true);
  };

  // Handle authority submit confirmation
  const handleAuthoritySubmitConfirm = async () => {
    if (!complaint) return;

    const remarks = document.getElementById('auth-edit-remarks')?.value || '';
    const formData = {
      assign_complain_type: selectedType,
      selected_template: selectedTemplate,
      remark: remarks,
    };

    // Call onSave with the form data
    if (onSave) {
      await onSave(formData);
    }

    setShowSubmitConfirmDialog(false);
  };

  // Handle template preview
  const handleTemplatePreview = async () => {
    if (!selectedTemplate || selectedTemplate === 'no-template') return;

    try {
      const templateContent = await templateService.loadTemplate(selectedTemplate);
      const templateData = {
        complainant_name: complaint.name || complaint.complainant || 'Complainant',
        complaint_id: complaint.complaint_id || complaint.id || '',
        date_registered: complaint.created_at ? new Date(complaint.created_at).toLocaleDateString() : new Date().toLocaleDateString(),
        status: complaint.status || '',
        type: selectedType || '',
        remarks: document.getElementById('auth-edit-remarks')?.value || '',
        current_date: new Date().toLocaleDateString(),
      };

      const renderedTemplate = templateService.renderTemplate(templateContent, templateData);
      setTemplatePreviewContent(renderedTemplate);
      setShowTemplatePreview(true);
    } catch (error) {
      console.error('Error loading template:', error);
    }
  };

  // Handle template download as PDF
  const handleTemplateDownloadPDF = async () => {
    if (!selectedTemplate || selectedTemplate === 'no-template') return;

    try {
      const templateContent = await templateService.loadTemplate(selectedTemplate);
      const templateData = {
        complainant_name: complaint.name || complaint.complainant || 'Complainant',
        complaint_id: complaint.complaint_id || complaint.id || '',
        date_registered: complaint.created_at ? new Date(complaint.created_at).toLocaleDateString() : new Date().toLocaleDateString(),
        status: complaint.status || '',
        type: selectedType || '',
        remarks: document.getElementById('auth-edit-remarks')?.value || '',
        current_date: new Date().toLocaleDateString(),
      };

      const renderedTemplate = templateService.renderTemplate(templateContent, templateData);
      const filename = `complaint-${complaint.complaint_id || complaint.id || 'document'}.pdf`;
      await templateService.generatePDF(renderedTemplate, filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  // Check if type is "Has vigilance angle"
  const isHasVigilanceAngle = normalizeString(selectedType) === 'has vigilance angle';

  const sortedLogs = useMemo(() => {
    if (!Array.isArray(complaintLogs)) return [];
    return [...complaintLogs].sort((a, b) => {
      const dateA = new Date(a?.created_at || 0).getTime();
      const dateB = new Date(b?.created_at || 0).getTime();
      return dateB - dateA;
    });
  }, [complaintLogs]);

  const previewLogs = useMemo(() => sortedLogs.slice(0, 2), [sortedLogs]);

  if (!isOpen || !complaint) return null;

  // Use separated documents if available, otherwise fall back to documents array
  const supportingDocuments = complaint.supporting_documents || [];
  const generalDocuments = complaint.general_documents || [];
  const hasSeparatedDocs = complaint.supporting_documents !== undefined || complaint.general_documents !== undefined;
  const allDocuments = complaint.documents || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Fixed Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900">
            Complaint: {complaint.complaint_id}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close dialog"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Complainant Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <p className="text-sm text-gray-900">{complaint.name || complaint.complainant}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
              <p className="text-sm text-gray-900">{complaint.mobile}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <p className="text-sm text-gray-900">{obfuscateEmail(complaint.email)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <p className="text-sm text-gray-900">{complaint.address}</p>
            </div>
          </div>

          {/* Complaint Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Complaint</label>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-900">{complaint.description || complaint.complaint}</p>
            </div>
          </div>

          {/* Supporting Documents */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Supporting Documents:</label>
            <div className="space-y-2">
              {supportingDocuments.length > 0 ? (
                supportingDocuments.map((doc, index) => {
                  const filePath = typeof doc === 'string' ? doc : doc.file_path;
                  const fileName = filePath?.split('/').pop() || 'Document';
                  const fileUrl = getFileUrl(filePath);
                  return (
                    <div key={doc.id || index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg">
                      <span className="text-sm text-gray-700 truncate flex-1">{fileName}</span>
                      <button
                        type="button"
                        onClick={() => handlePreviewFile(filePath, fileName)}
                        className="text-blue-600 hover:text-blue-800 ml-2 flex-shrink-0 p-1 hover:bg-blue-50 rounded-full transition-colors"
                        aria-label="View document"
                        title="View Document"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-gray-500">No supporting documents uploaded</p>
              )}
            </div>
          </div>

          {/* General Documents */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">General Documents:</label>
            <div className="space-y-2">
              {generalDocuments.length > 0 ? (
                generalDocuments.map((doc, index) => {
                  const filePath = typeof doc === 'string' ? doc : doc.file_path;
                  const fileName = filePath?.split('/').pop() || 'Document';
                  const fileUrl = getFileUrl(filePath);
                  return (
                    <div key={doc.id || index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg">
                      <span className="text-sm text-gray-700 truncate flex-1">{fileName}</span>
                      <button
                        type="button"
                        onClick={() => handlePreviewFile(filePath, fileName)}
                        className="text-blue-600 hover:text-blue-800 ml-2 flex-shrink-0 p-1 hover:bg-blue-50 rounded-full transition-colors"
                        aria-label="View document"
                        title="View Document"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-gray-500">No general documents uploaded</p>
              )}
            </div>
          </div>

          {/* Fallback: All Documents (if separated not available) */}
          {!hasSeparatedDocs && allDocuments.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">All Documents:</label>
              <div className="space-y-2">
                {allDocuments.map((doc, index) => {
                  const filePath = typeof doc === 'string' ? doc : doc.file_path;
                  const fileName = filePath?.split('/').pop() || 'Document';
                  const fileUrl = getFileUrl(filePath);
                  return (
                    <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg">
                      <span className="text-sm text-gray-700 truncate flex-1">{fileName}</span>
                      <button
                        type="button"
                        onClick={() => handlePreviewFile(filePath, fileName)}
                        className="text-blue-600 hover:text-blue-800 ml-2 flex-shrink-0 p-1 hover:bg-blue-50 rounded-full transition-colors"
                        aria-label="View document"
                        title="View Document"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* CVO Fields (Disabled/Read-only) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assigned Officer (by CVO)
              </label>
              <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed">
                <span className="text-sm text-gray-700">
                  {complaint?.assigned_to?.name || 'Not assigned'}
                </span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status (by CVO)
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                disabled={true}
                value={(() => {
                  const matchingStatus = complaintStatuses.find(s => isSelected(getOptionValue(s), complaint.status));
                  return matchingStatus ? getOptionValue(matchingStatus) : (complaint.status || '');
                })()}
              >
                {complaintStatuses.map((s, idx) => {
                  const optionValue = getOptionValue(s);
                  return (
                    <option key={idx} value={optionValue}>
                      {optionValue}
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Department (by CVO)
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                disabled={true}
                value={(() => {
                  const matchingDept = departments.find(d => isSelected(getOptionValue(d), complaint.department));
                  return matchingDept ? getOptionValue(matchingDept) : (complaint.department || '');
                })()}
              >
                {departments.map((d, idx) => {
                  const optionValue = getOptionValue(d);
                  return (
                    <option key={idx} value={optionValue}>
                      {optionValue}
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority (by CVO)
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                disabled={true}
                value={(() => {
                  const matchingPriority = priorities.find(p => isSelected(getOptionValue(p), complaint.priority));
                  return matchingPriority ? getOptionValue(matchingPriority) : (complaint.priority || '');
                })()}
              >
                {priorities.map((p, idx) => {
                  const optionValue = getOptionValue(p);
                  return (
                    <option key={idx} value={optionValue}>
                      {optionValue}
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assigned Days (by CVO)
              </label>
              <input
                type="number"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                disabled={true}
                value={complaint.assigned_day_by_cvo || ''}
                readOnly
              />
            </div>
          </div>

          {/* Type Field (Editable by Authority - disabled if type already selected) */}
          <div>
            <label htmlFor="auth-edit-type" className="block text-sm font-medium text-gray-700 mb-2">
              Type
            </label>
            <select
              id="auth-edit-type"
              value={selectedType || ''}
              onChange={(e) => {
                const newType = e.target.value;
                setSelectedType(newType);
              }}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-primary-400 ${isFormDisabled ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
              disabled={loading || isFormDisabled}
            >
              <option value="">Select Type</option>
              {complaintTypes.map((t, idx) => {
                const optionValue = getOptionValue(t);
                return (
                  <option key={idx} value={optionValue}>
                    {optionValue}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Template Selection (based on Type) - Only visible when type is selected and form is not disabled */}
          {selectedType && templateOptions.length > 0 && !isFormDisabled && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Template
              </label>
              <div className="space-y-2">
                {templateOptions.map((template) => (
                  <label key={template.value} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="template"
                      value={template.value}
                      checked={selectedTemplate === template.value}
                      onChange={(e) => setSelectedTemplate(e.target.value)}
                      className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                      disabled={loading || template.value === 'no-template'}
                    />
                    <span className="text-sm text-gray-700">{template.label}</span>
                  </label>
                ))}
              </div>
              {selectedTemplate && selectedTemplate !== 'no-template' && (
                <div className="mt-3 flex space-x-3 items-center">
                  <button
                    type="button"
                    onClick={handleTemplatePreview}
                    className="p-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={loading}
                    title="Preview Template"
                    aria-label="Preview Template"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={handleTemplateDownloadPDF}
                    className="p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={loading}
                    title="Download PDF"
                    aria-label="Download PDF"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Template Selection (Read-only when type is already selected) */}
          {selectedType && templateOptions.length > 0 && isFormDisabled && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Selected Template
              </label>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <span className="text-sm text-gray-700">
                  {complaint?.template_type || 'No template selected'}
                </span>
              </div>
            </div>
          )}

          {/* Remarks Field - Always editable */}
          <div>
            <label htmlFor="auth-edit-remarks" className="block text-sm font-medium text-gray-700 mb-2">
              Remarks
            </label>
            <textarea
              id="auth-edit-remarks"
              rows={3}
              defaultValue={complaint.remark || complaint.remarks || ''}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
              placeholder="Add remarks..."
              disabled={loading}
            />
          </div>

          {/* Submit Button - Hidden when type is already selected (hasExistingType) */}
          {!hasExistingType && (
            <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
              <button
                onClick={handleSubmitClick}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={loading || !hasSelectedType}
              >
                {loading ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          )}

          {/* Add Internal Note - Only visible when type is "Has vigilance angle" AND type is already selected (view mode) */}
          {isHasVigilanceAngle && hasExistingType && (
            <div>
              <h3 className="text-md font-semibold text-gray-900 mb-4">Add Internal Note</h3>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="space-y-3">
                  <div>
                    <label htmlFor="auth-note-recipient" className="block text-sm font-medium text-gray-700 mb-2">
                      Send To
                    </label>
                    <MultiSelect
                      id="auth-note-recipient"
                      options={[
                        ...officers.filter(o => o.status === 'Active').map(officer => ({
                          id: `officer-${officer.id}`,
                          name: officer.name
                        })),
                        {
                          id: 'complainant',
                          name: `${complaint.complainant || complaint.name || 'Complainant'} - Complainer`
                        }
                      ]}
                      value={selectedRecipients}
                      onChange={setSelectedRecipients}
                      placeholder="Select recipients..."
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label htmlFor="auth-note-message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message
                    </label>
                    <textarea
                      id="auth-note-message"
                      rows={3}
                      value={newNote}
                      onChange={(e) => onNoteChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                      placeholder="Enter internal note..."
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label htmlFor="auth-note-attachments" className="block text-sm font-medium text-gray-700 mb-2">
                      Attachments (Optional)
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                      <input
                        type="file"
                        id="auth-note-attachments"
                        multiple
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          if (files.length > 0) {
                            setNoteAttachments(prev => [...prev, ...files]);
                          }
                          // Reset input to allow selecting same file again
                          e.target.value = '';
                        }}
                        className="hidden"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx"
                        disabled={loading}
                      />
                      <label htmlFor="auth-note-attachments" className="cursor-pointer flex flex-col items-center">
                        <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <span className="text-sm text-gray-600">Click to upload documents</span>
                        <span className="text-xs text-gray-500 mt-1">PDF, DOC, DOCX, JPG, PNG, XLS, XLSX</span>
                      </label>
                    </div>
                    {noteAttachments.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {noteAttachments.map((file, index) => (
                          <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg">
                            <div className="flex items-center space-x-2 flex-1 min-w-0">
                              <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <span className="text-sm text-gray-700 truncate">{file.name}</span>
                              <span className="text-xs text-gray-500">({(file.size / 1024).toFixed(1)} KB)</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => window.open(URL.createObjectURL(file), '_blank')}
                              className="ml-2 text-blue-600 hover:text-blue-800 flex-shrink-0"
                              disabled={loading}
                              title="View file"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => setNoteAttachments(prev => prev.filter((_, i) => i !== index))}
                              className="ml-2 text-red-600 hover:text-red-800 flex-shrink-0"
                              disabled={loading}
                              title="Remove file"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        onAddNote(noteAttachments, selectedRecipients);
                        setNoteAttachments([]);
                        setSelectedRecipients([]);
                      }}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      disabled={loading || (!newNote.trim() && noteAttachments.length === 0)}
                    >
                      {loading && (
                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      )}
                      <span>{loading ? 'Sending...' : 'Add to History'}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* History Log Section - Last block */}
          {showHistory && (
            <div>
              <h3 className="text-md font-semibold text-gray-900 mb-4">History Log</h3>
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                {logsLoading ? (
                  <p className="text-sm text-gray-600">Loading complaint history...</p>
                ) : sortedLogs.length === 0 ? (
                  <p className="text-sm text-gray-500">No history available for this complaint yet.</p>
                ) : (
                  <div className="space-y-3">
                    {previewLogs.map((log) => (
                      <HistoryEntryCard key={log.id} log={log} />
                    ))}
                  </div>
                )}
                {sortedLogs.length > 2 && (
                  <button
                    type="button"
                    onClick={() => setIsHistoryDialogOpen(true)}
                    className="mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    View full history ({sortedLogs.length} entries)
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Fixed Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            disabled={loading}
          >
            Close
          </button>
        </div>
      </div>

      {/* Authority Submit Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showSubmitConfirmDialog}
        title="Confirm Submission"
        message={
          isHasVigilanceAngle
            ? `Are you sure you want to submit this complaint? Since this complaint has a vigilance angle, further conversation can be done through "Add Internal Note" section.`
            : "Are you sure you want to submit this complaint?"
        }
        onConfirm={handleAuthoritySubmitConfirm}
        onCancel={() => setShowSubmitConfirmDialog(false)}
        loading={loading}
        confirmText="Submit"
        cancelText="Cancel"
        confirmVariant="primary"
      />

      {/* Template Preview Dialog */}
      {showTemplatePreview && (
        <BaseDialog
          isOpen={showTemplatePreview}
          onClose={() => setShowTemplatePreview(false)}
          title="Template Preview"
          size="large"
        >
          <div className="space-y-4">
            <div
              className="border border-gray-200 rounded-lg p-4 bg-white"
              dangerouslySetInnerHTML={{ __html: templatePreviewContent }}
            />
            <div className="flex justify-end space-x-2">
              <Button variant="secondary" onClick={() => setShowTemplatePreview(false)}>
                Close
              </Button>
              <Button variant="primary" onClick={handleTemplateDownloadPDF}>
                Download PDF
              </Button>
            </div>
          </div>
        </BaseDialog>
      )}

      <ComplaintHistoryDialog
        isOpen={isHistoryDialogOpen}
        onClose={() => setIsHistoryDialogOpen(false)}
        logs={sortedLogs}
      />

      <FilePreviewDialog
        isOpen={previewDialog.isOpen}
        onClose={closePreviewDialog}
        fileUrl={previewDialog.fileUrl}
        fileName={previewDialog.fileName}
        fileType={previewDialog.fileType}
      />
    </div>
  );
};

export default AuthorityEditComplaintDialog;

const formatLogDate = (dateString) => {
  if (!dateString) return '—';
  const parsed = new Date(dateString);
  if (Number.isNaN(parsed.getTime())) {
    return dateString;
  }
  return parsed.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const formatActionType = (actionType) => {
  if (!actionType) return 'Update';
  return actionType
    .toString()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const HistoryEntryCard = ({ log }) => {
  const {
    id,
    action_type,
    remark,
    created_at,
    old_value,
    new_value,
    action_by_role,
    action_by,
    complaint_status,
    assigned_to,
  } = log || {};

  const hasValueChange = old_value !== undefined && old_value !== null && new_value !== undefined && new_value !== null;

  return (
    <div key={id} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-primary-100 text-primary-700">
            {formatActionType(action_type)}
          </span>
          <div className="mt-1 text-sm text-gray-700">
            <span className="font-medium">Remark:</span> {remark || 'No remarks provided.'}
          </div>
        </div>
        <span className="text-xs text-gray-500">{formatLogDate(created_at)}</span>
      </div>
      <HistoryDetailGrid
        hasValueChange={hasValueChange}
        oldValue={old_value}
        newValue={new_value}
        complaintStatus={complaint_status}
        assignedTo={assigned_to}
        actionBy={action_by}
        actionByRole={action_by_role}
      />
    </div>
  );
};

const ComplaintHistoryDialog = ({ isOpen, onClose, logs }) => (
  <BaseDialog isOpen={isOpen} onClose={onClose} title="Complaint History & Log" size="large">
    {Array.isArray(logs) && logs.length > 0 ? (
      <div className="space-y-3">
        {logs.map((log) => (
          <HistoryEntryCard key={log.id} log={log} />
        ))}
      </div>
    ) : (
      <p className="text-sm text-gray-500">No history available for this complaint yet.</p>
    )}
    <div className="flex justify-end pt-4">
      <Button variant="secondary" onClick={onClose}>
        Close
      </Button>
    </div>
  </BaseDialog>
);

const HistoryDetailGrid = ({
  hasValueChange,
  oldValue,
  newValue,
  complaintStatus,
  assignedTo,
  actionBy,
  actionByRole,
}) => {
  const detailItems = [];

  if (hasValueChange) {
    detailItems.push(
      { label: 'Previous Value', value: oldValue || '—' },
      { label: 'Updated Value', value: newValue || '—' }
    );
  }

  if (complaintStatus) {
    detailItems.push({ label: 'Status', value: complaintStatus });
  }

  if (assignedTo) {
    detailItems.push({ label: 'Assigned To', value: assignedTo });
  }

  const actionByValue = [actionByRole, actionBy].filter(Boolean).join(' - ');
  if (actionByValue) {
    detailItems.push({ label: 'Action By', value: actionByValue });
  }

  if (detailItems.length === 0) {
    return null;
  }

  return (
    <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-2 gap-x-4 text-xs text-gray-600">
      {detailItems.map((item, idx) => (
        <div key={`${item.label}-${idx}`}>
          <dt className="font-medium text-gray-700">{item.label}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
};

